<script lang="ts">
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import SbRichtext from '../components/sbRichtext.svelte';
	import type { SpacerSize } from '../types';
	import { editable } from '../editable';

	interface Props {
		content: ISbStoryData<{
			text: ISbRichtext;
			align: 'center' | 'left';
			text_color: string;
			margin_top: SpacerSize;
			margin_bottom: SpacerSize;
			font_serif: boolean;
		}>['content'];
	}

	let { content }: Props = $props();
</script>

<div
	use:editable={content}
	class="sb-richtext w-full text-balance text-[16px]/[140%] text-{content.text_color ||
		'brown-1'} text-{content.align || 'center'}"
	class:mt-3={content.margin_top === 'small'}
	class:mb-3={content.margin_bottom === 'small'}
	class:mt-6={content.margin_top === 'medium'}
	class:mb-6={content.margin_bottom === 'medium'}
	class:mt-12={content.margin_top === 'large'}
	class:mb-12={content.margin_bottom === 'large'}
	class:font-serif={content.font_serif}
	style="--weight: {content.font_serif ? '400' : '600'}"
>
	<SbRichtext text={content.text} />
</div>

<style lang="scss">
	:global(.sb-semibold) {
		font-weight: 600 !important;
	}

	:global(.sb-font-normal) {
		font-weight: 400 !important;
	}

	:global(.sb-big-text) {
		font-size: 1.125rem;
		line-height: 120%;
		text-wrap: balance;

		@media (max-width: 1023px) {
			letter-spacing: -0.0225rem;
		}

		@media (min-width: 1024px) {
			font-size: 24px;
			line-height: 140%;
		}
	}

	div.sb-richtext {
		:global(h1),
		:global(h2),
		:global(h3),
		:global(h4),
		:global(h5) {
			font-style: normal;
			font-weight: var(--weight);
			line-height: 110%;

			&:global(:not(:last-child)) {
				margin-bottom: theme('spacing.1');
			}
		}
		:global(h1) {
			font-size: 32px;
			letter-spacing: -0.32px;

			@media (min-width: 1024px) {
				font-size: 56px;
				letter-spacing: -0.56px;
			}
		}
		:global(h2) {
			font-size: 20px;

			@media (min-width: 1024px) {
				font-size: 28px;
			}
		}
		:global(h3) {
			font-size: 20px;

			@media (min-width: 1024px) {
				font-size: 18px;
			}
		}
		:global(h4) {
			font-size: 40px;

			@media (min-width: 1024px) {
				font-size: 64px;
			}
		}
		:global(h5) {
			font-size: 56px;

			@media (min-width: 1024px) {
				font-size: 64px;
			}
		}
		:global(h6) {
			font-size: 14px;

			@media (min-width: 1024px) {
				font-size: 16px;
			}
		}
		:global(ol),
		:global(ul),
		:global(p) {
			&:global(:not(:last-child)) {
				margin-bottom: theme('spacing.1');
			}
		}
		:global(ul),
		:global(ol) {
			padding-left: 20px;
		}

		:global(p),
		:global(ol li),
		:global(ul li) {
			font-size: 16px;
			line-height: 140%;
		}
		:global(li p) {
			display: inline;
		}
		:global(a) {
			text-decoration: underline;
			font-weight: var(--weight);
		}

		:global(ul li) {
			list-style-type: disc;
		}

		:global(ol li) {
			list-style-type: decimal;
		}
	}
</style>
